import { useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { Box, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router';

import { BasePage } from 'hydra/pages/BasePage';
import {
    TrainingDocument,
    ITrainingRole,
    IRole,
    CurrentUserDocument,
} from 'graphql/types';
import { Loader } from 'common/components/Loader';
import { useTrainingTabs } from 'training/hooks';
import { TrainingTabsMenu } from 'training/components/TrainingTabsMenu';
import { PageIntroHeader } from 'common/components/PageIntroHeader';
import { isUUID } from 'common/utils/isUUID';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { ModuleCreateDrawer } from 'training/components/ModuleCreateDrawer';
import { ModuleUpdateDrawer } from 'training/components/ModuleUpdateDrawer';
import { TrainingInfo } from 'training/components/TrainingInfo';
import { gotoTrainingPage } from 'training/utils/gotoRoutes';
import { TrainingContextActionBar } from 'training/components/TrainingContextActionBar';
import { InlineStickyWrapper } from 'common/components/InlineStickyWrapper';
import { getTrainingPermissions } from 'training/utils/permissions';
import { trainingStudentView } from 'training/state/trainingStudentView';

import { FourOFourPage } from './FourOFourPage';

export const TrainingPage = () => {
    const navigate = useNavigate();
    const { trainingId } = useParams() as { trainingId: string };

    const { data, loading } = useQuery(TrainingDocument, {
        variables: { id: trainingId },
        nextFetchPolicy: 'cache-only',
    });
    const { data: currentUserData } = useQuery(CurrentUserDocument);

    const isManager = !!currentUserData?.currentUser?.roles?.includes(
        IRole.Manager
    );

    const training = data?.training;

    const {
        canUpdate: canUpdateTraining = false,
        canReadParticipants = false,
        canReadGroups = false,
    } = getTrainingPermissions(training);
    const { Section, tabs } = useTrainingTabs(trainingId, training);

    const isParticipantsPage = !!useRouteMatch('TRAINING_PARTICIPANTS');
    const isGroupsPage = !!useRouteMatch('TRAINING_GROUPS');
    const isTrainersPage = !!useRouteMatch('TRAINING_TRAINERS');
    const isSharedFilesPage = !!useRouteMatch('TRAINING_SHARED_FILES');

    const showPreviewButton =
        !isParticipantsPage && !isTrainersPage && !isSharedFilesPage;

    const isCreateModulePage = !!useRouteMatch('TRAINING_CREATE_MODULE');
    const isCreateModuleTypePage = !!useRouteMatch(
        'TRAINING_CREATE_MODULE_TYPE'
    );
    const isCreateModuleInGroupPage = !!useRouteMatch(
        'TRAINING_CREATE_MODULE_IN_GROUP'
    );

    const isCreateModuleTypeInGroupPage = !!useRouteMatch(
        'TRAINING_CREATE_MODULE_TYPE_IN_GROUP'
    );

    const isModuleGroupCreatePage = !!useRouteMatch(
        'TRAINING_CREATE_MODULE_GROUP'
    );
    const isModuleGroupEditPage = !!useRouteMatch('TRAINING_EDIT_MODULE_GROUP');

    const isUpdateModulePage = !!useRouteMatch('TRAINING_UPDATE_MODULE');

    const { rolesForCurrentUser } = training || {};

    const isTrainer =
        !!rolesForCurrentUser?.includes(ITrainingRole.Trainer) ||
        !!rolesForCurrentUser?.includes(ITrainingRole.Mentor);
    const isGroupmanager = !!rolesForCurrentUser?.includes(
        ITrainingRole.Groupmanager
    );

    // canManage are user who can update training can see conditions effects, rate assignments, etc,
    const canManageTraining = !!(
        isTrainer ||
        isGroupmanager ||
        canUpdateTraining
    );

    const canManageParticipants = !!(isTrainer || isGroupmanager || isManager);

    // Only users who can edit the training (canUpdate) get the manage view; the
    // preview toggle is editor-only too. Trainers and groupmanagers open in and
    // stay in student view, so they are never dropped into a manage view they
    // cannot use or leave.
    useEffect(() => {
        trainingStudentView(!canUpdateTraining);
    }, [canUpdateTraining]);

    if (!isUUID(trainingId)) {
        return <FourOFourPage />;
    }

    // Render 404 when training is not found
    if (!loading && data !== undefined && !training) {
        return <FourOFourPage />;
    }

    // When on a module group create or update route and can't manage
    const unauthorizedModuleGroupEdit =
        (isModuleGroupCreatePage || isModuleGroupEditPage) &&
        !canManageTraining;

    const unauthorizedParticipantsView =
        isParticipantsPage && !!training && !canReadParticipants;
    const unauthorizedGroupsView = isGroupsPage && !!training && !canReadGroups;

    if (
        unauthorizedModuleGroupEdit ||
        unauthorizedParticipantsView ||
        unauthorizedGroupsView
    ) {
        return <FourOFourPage />;
    }

    const {
        modelId,
        title,
        subtitle,
        titleColor: trainingTitleColor,
        image,
        conditions,
        estimatedTimeSpent,
        extraTrainingFieldsLabels,
    } = training || {};

    const titleColor = trainingTitleColor && `#${trainingTitleColor}`;

    let headerColor = titleColor;

    if (!headerColor) {
        headerColor = image ? 'common.white' : undefined;
    }

    const trainingInfo = (
        <TrainingInfo
            conditions={conditions}
            estimatedTimeSpent={estimatedTimeSpent}
            extraTrainingFieldsLabels={extraTrainingFieldsLabels}
            sx={{ color: headerColor }}
        />
    );

    const showModuleCreateDrawer =
        isCreateModulePage ||
        isCreateModuleTypePage ||
        isCreateModuleInGroupPage ||
        isCreateModuleTypeInGroupPage;

    let header;

    if (image) {
        header = (
            <PageIntroHeader fixed={isParticipantsPage} image={image}>
                <Typography sx={{ color: headerColor }} variant="h1">
                    {title}
                </Typography>
                <Typography
                    sx={{
                        color: headerColor,
                    }}
                    variant="subtitle1"
                >
                    {subtitle}
                </Typography>

                {trainingInfo}
            </PageIntroHeader>
        );
    }

    return (
        <BasePage
            contextActionBar={
                training && (
                    <TrainingContextActionBar
                        canUpdateTraining={!!canUpdateTraining}
                        showPreviewButton={showPreviewButton}
                        training={training}
                    />
                )
            }
            header={header}
        >
            {loading && <Loader />}
            {!loading && training && (
                <Box
                    sx={[
                        {
                            pb: 8,
                            pt: conditions || header ? 4 : { xs: 4, sm: 8 },
                        },
                        isParticipantsPage && { width: 'max-content' },
                    ]}
                >
                    <InlineStickyWrapper fullWidth leftOffset={3}>
                        {!header && (
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        sx={{ color: headerColor }}
                                        variant="h1"
                                    >
                                        {title}
                                    </Typography>
                                </Box>
                                {subtitle && (
                                    <Typography
                                        sx={{ color: headerColor }}
                                        variant="subtitle1"
                                    >
                                        {subtitle}
                                    </Typography>
                                )}

                                {trainingInfo}
                            </Box>
                        )}

                        {tabs.length > 1 && <TrainingTabsMenu tabs={tabs} />}
                    </InlineStickyWrapper>

                    {Section && (
                        <Box sx={{ pt: 2 }}>
                            <Section
                                canManage={canManageTraining}
                                canManageParticipants={canManageParticipants}
                                training={training}
                            />
                        </Box>
                    )}

                    {canUpdateTraining && (
                        <>
                            <ModuleCreateDrawer
                                open={showModuleCreateDrawer}
                                trainingId={trainingId}
                                trainingModelId={modelId || undefined}
                                onClose={() => {
                                    gotoTrainingPage(navigate, trainingId);
                                }}
                            />
                            <ModuleUpdateDrawer
                                open={isUpdateModulePage}
                                onClose={() => {
                                    gotoTrainingPage(navigate, trainingId);
                                }}
                            />
                        </>
                    )}
                </Box>
            )}
        </BasePage>
    );
};
